
import React, { useState, useMemo, useRef } from 'react';
import { Play, Square, Loader2, Settings } from 'lucide-react';
import { tradingApi, SystemControl } from '../services/api';

interface GatewayControlButtonProps {
  controlState: SystemControl | null;
  onRefresh: () => Promise<void>;
  onConfigureNeeded: () => void;
  className?: string;
  onError: (error: string) => void;
  variant?: 'compact' | 'slim';
  gatewayId?: string;
  iconSize?: number;
}

const GatewayControlButton: React.FC<GatewayControlButtonProps> = ({
  controlState,
  onRefresh,
  onConfigureNeeded,
  className,
  onError,
  variant = 'compact',
  gatewayId,
  iconSize: iconSizeProp,
}) => {
  const [loadingAction, setLoadingAction] = useState<"start" | "stop" | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [pendingHint, setPendingHint] = useState<string | null>(null);
  const hintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFatalConnectionError = (msg?: string) => {
    if (!msg) return false;
    const lower = msg.toLowerCase();
    return (
      lower.includes("invalid credential") ||
      lower.includes("trading mode mismatch") ||
      lower.includes("returned exit status") ||
      lower.includes("failed to decrypt") ||
      lower.includes("fatal")
    );
  };

  // Keep local hint in sync with control state (e.g., from periodic refreshes)
  React.useEffect(() => {
    // Show hint if gateway is in a pending state (not connected but has hint)
    if (controlState?.gateway_hint && !controlState.ib_connected && controlState.gateway_running) {
      setPendingHint(controlState.gateway_hint);
    }
    // Clear hint if connected or gateway is not running (stopped or not set)
    if (controlState?.ib_connected || !controlState?.gateway_running) {
      setPendingHint(null);
    }
  }, [controlState?.gateway_hint, controlState?.ib_connected, controlState?.gateway_running]);

  const buttonsTheme = useMemo(
    () => ({
      // Base styles common to all variants (colors, borders, transitions)
      base: "rounded-xl font-semibold transition-all duration-200 border",
      // Layout styles for Compact (Grid) view
      compact: "col-span-1 w-full px-3 py-3 flex flex-col items-center justify-center gap-1.5 text-xs sm:text-sm",
      // Layout styles for Slim (Row) view
      slim: "flex items-center justify-center gap-1.5 rounded-lg",

      start:
        "bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 border-emerald-500/20 shadow-sm",
      stop: "bg-rose-500/15 text-rose-400 hover:bg-rose-500/25 border-rose-500/20 shadow-sm",
      loading:
        "bg-warning-500/15 text-warning-400 hover:bg-warning-500/25 border-warning-500/20 shadow-[0_0_15px_rgba(245,158,11,0.05)] cursor-wait",
    }),
    []
  );

  const handleGatewayControl = async (action: "start" | "stop") => {
    if (!controlState) return;
    const isLiveMode = controlState.ib_mode === 'live';

    // Smart Start Logic: Check if configured before starting
    // If specific gatewayId is provided, we assume it's configured (or we can't check easily without iterating saved_gateways)
    if (action === "start" && !gatewayId && !controlState.gateway_configured) {
      onConfigureNeeded();
      return;
    }

    // Cancel any previous pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setPendingHint(null);
    setLoadingAction(action);

    try {
      const response = await tradingApi.controlIBGateway(action, gatewayId);

      if (response.success) {
        if (action === "start") {
          // Show a generic reminder after a short delay in live mode
          if (isLiveMode && !controlState.ib_connected) {
            // Show immediately so the user sees it even if backend retries/DNS take time
            setPendingHint("Gateway is starting. Approve IB Key on your phone if prompted.");
            if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
            hintTimerRef.current = setTimeout(() => {
              setPendingHint((current) => current || "Gateway is starting. Approve IB Key on your phone if prompted.");
            }, 5000);
          }

          // Poll for connection status
          let attempts = 0;
          const baseMaxAttempts = 30; // 30 seconds timeout
          let maxAttempts = baseMaxAttempts;
          let lastConnectionError: string | null = null;

          while (attempts < maxAttempts && abortControllerRef.current === controller) {
            await onRefresh();
            // We need to check the fresh state, but onRefresh updates the parent's state.
            // We can't easily access the *new* prop value here immediately inside the loop 
            // because of closure staleness, unless we trust onRefresh to update the prop 
            // and we rely on the re-render? No, re-render won't update 'controlState' in this scope.
            // We need to fetch the state directly or wait for the parent to update.
            // Better: Fetch state directly here to check.

            try {
              const status = await tradingApi.getSystemControlState();

              const statusData = status.data;

              // Update hint state from backend (e.g. 2FA)
              if (status.success && statusData?.gateway_hint) {
                setPendingHint(statusData.gateway_hint);
                // Extend wait window if user action is pending
                maxAttempts = Math.max(maxAttempts, 180);
              }

              // Check for explicit errors from the backend (e.g. invalid credentials)
              if (status.success && statusData?.connection_error) {
                lastConnectionError = statusData.connection_error;
                if (isFatalConnectionError(statusData.connection_error)) {
                  onError(statusData.connection_error);
                  // Stop the gateway to clean up
                  await tradingApi.controlIBGateway("stop");
                  break;
                } else {
                  // Treat as transient (DNS/startup). Keep waiting but extend window.
                  setPendingHint(statusData.connection_error);
                  maxAttempts = Math.max(maxAttempts, 180);
                }
              }

              if (status.success && statusData?.ib_connected) {
                setPendingHint(null);
                break;
              }
            } catch (e) {
              console.warn("Failed to poll status", e);
            }

            await new Promise(resolve => setTimeout(resolve, 1000));
            attempts++;
          }

          if (attempts >= maxAttempts) {
            if (pendingHint || lastConnectionError) {
              onError(
                pendingHint ||
                lastConnectionError ||
                "Gateway is waiting for approval/startup. Please check the gateway and IB Key."
              );
            } else {
              onError("Gateway start is taking longer than expected. Check gateway container status.");
            }
            // Do not auto-stop on timeout; allow backend retries / user approval.
          }
          // Final refresh to update UI
          await onRefresh();

        } else {
          // For stop, clear hint and refresh
          setPendingHint(null);
          await onRefresh();
        }
      } else {
        onError(response.message || `Failed to ${action} gateway`);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.log('Gateway control action cancelled');
      } else {
        onError(
          err instanceof Error ? err.message : `Failed to ${action} gateway`
        );
      }
    } finally {
      if (abortControllerRef.current === controller) {
        setLoadingAction(null);
        abortControllerRef.current = null;
      }
      if (hintTimerRef.current) {
        clearTimeout(hintTimerRef.current);
        hintTimerRef.current = null;
      }
    }
  };

  const handleCancel = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const wasStarting = !controlState?.ib_connected;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setLoadingAction(null);

    if (wasStarting) {
      try {
        // Explicitly stop the gateway to ensure cleanup
        await tradingApi.controlIBGateway("stop", gatewayId);
        await onRefresh();
      } catch (err) {
        console.warn("Failed to stop gateway during cancellation", err);
      }
    }
  };

  if (!controlState) return null;

  const isConnecting = controlState.gateway_hint_code === "connecting";
  const showLoading = (loadingAction !== null) || isConnecting;

  // Determine gateway active state: prefer gateway_running (container status), fall back to ib_connected
  const isGatewayActive = controlState.gateway_running ?? controlState.ib_connected;



  const layoutClass = variant === 'compact' ? buttonsTheme.compact : buttonsTheme.slim;
  const colorClass = showLoading
    ? buttonsTheme.loading
    : (isGatewayActive ? buttonsTheme.stop : buttonsTheme.start);

  const isConfigured = controlState.gateway_configured;
  const actionLabel = isGatewayActive
    ? "Disconnect"
    : isConfigured
      ? "Connect"
      : "Set up broker";

  const iconSize = iconSizeProp ?? (variant === 'slim' ? 16 : 18);

  return (
    <button
      onClick={() => {
        if (showLoading) return;
        handleGatewayControl(isGatewayActive ? "stop" : "start");
      }}
      disabled={loadingAction === 'stop'}
      className={`${buttonsTheme.base} ${layoutClass} ${colorClass} ${className || ''} relative group disabled:opacity-50 disabled:cursor-not-allowed`}
      title={!isConfigured && !isGatewayActive ? "Set up the broker connection" : isGatewayActive ? "Disconnect the broker gateway" : "Connect to the broker gateway"}
      aria-label={showLoading ? (loadingAction === 'stop' ? "Disconnecting from broker" : "Connecting to broker") : actionLabel}
    >
      {showLoading ? (
        <>
          <Loader2 size={iconSize} className="animate-spin" />
          {variant === 'slim' ? (
            <div className="flex items-center gap-1 leading-none">
              <span>
                {loadingAction === 'stop' ? "Disconnecting..." : "Connecting..."}
              </span>
              {loadingAction !== 'stop' && (
                <span
                  onClick={handleCancel}
                  className="text-[9px] text-red-400 hover:text-red-300 underline cursor-pointer ml-1 z-10"
                >
                  (Cancel)
                </span>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center leading-none">
              <span>
                {loadingAction === 'stop'
                  ? "Disconnecting..."
                  : (loadingAction === 'start' || isConnecting)
                    ? "Connecting..."
                    : (isGatewayActive ? "Disconnecting..." : "Connecting...")
                }
              </span>
              {loadingAction !== 'stop' && (
                <span
                  onClick={handleCancel}
                  className="text-[10px] text-red-400 hover:text-red-300 underline cursor-pointer mt-1 z-10"
                >
                  Cancel
                </span>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {!isGatewayActive && !isConfigured ? (
            <Settings size={iconSize} />
          ) : isGatewayActive ? (
            <Square size={iconSize} />
          ) : (
            <Play size={iconSize} />
          )}
          {variant === 'slim' ? (
            <span className="min-w-0 truncate leading-none font-bold">
              {actionLabel}
            </span>
          ) : (
            <div className="flex flex-col items-center leading-none">
              <span>
                {isGatewayActive ? "Disconnect" : "Connect to Broker"}
              </span>
              {isConfigured && controlState.ib_username && (
                <span className="text-[10px] opacity-70 font-normal mt-0.5">
                  {controlState.ib_username}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </button>
  );
};

export default GatewayControlButton;
